// frontend/src/pages/admin/Affiliates.js
//
// The operator's side of the partner programme: who is on it, what they have
// earned, and recording that they were paid.
//
// The payout button is the one control on this page that corresponds to money
// leaving a bank account, so it behaves accordingly: it demands a transfer
// reference before it will do anything, it settles only what is APPROVED (never
// what is still inside its hold window), and it confirms the exact figure back
// before it writes. Nothing here moves money — a human does that in their
// banking app — so the row this creates is a record of something that already
// happened, and it must not be creatable before it did.

import React, { useEffect, useState } from 'react';
import AdminSidebar from '../../components/AdminSidebar';
import api from '../../utils/api';
import { errorText } from '../../utils/errors';
import toast from 'react-hot-toast';
import { useTranslation } from 'react-i18next';
import { getIntlLocale } from '../../i18n';
import { Plus, X, Save, ToggleLeft, ToggleRight, Wallet, Eye } from 'lucide-react';
import theme from '../../theme';

const FONTS = '/fonts/fonts.css';
const money = (v) => `€${(parseFloat(v) || 0).toFixed(2)}`;

// The house rate for each kind of partner, prefilled so that signing up a salon
// on the standard deal is the path of least resistance and an unusual rate is a
// deliberate act. A shop is putting the customer in the chair and talking her
// through the set — the most expensive referral we buy, and the one worth the
// most. Nothing here is a ceiling: AFFILIATE_MAX_RATE on the server is, and it
// is what catches a 50 typed where 5 was meant.
const DEFAULT_RATE = { shop: '20', influencer: '10', customer: '10' };

const EMPTY_FORM = {
  kind: 'shop', name: '', contact_email: '', contact_phone: '', login_email: '',
  commission_type: 'percentage', commission_value: DEFAULT_RATE.shop, discount_code: '',
  payout_method: 'bank_transfer', payout_details: '', notes: '',
};

export default function AdminAffiliates() {
  const { t } = useTranslation();
  const dateLocale = getIntlLocale();

  const [affiliates, setAffiliates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState(EMPTY_FORM);
  const [saving, setSaving] = useState(false);
  const [detail, setDetail] = useState(null);
  const [payingOut, setPayingOut] = useState(false);

  useEffect(() => {
    const link = document.createElement('link');
    link.rel = 'stylesheet'; link.href = FONTS; document.head.appendChild(link);
    load();
    return () => document.head.removeChild(link);
  }, []);

  const load = () => {
    setLoading(true);
    api.get('/affiliates')
      .then(setAffiliates)
      .catch(() => toast.error(t('admin.loadError')))
      .finally(() => setLoading(false));
  };

  const create = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const created = await api.post('/affiliates', {
        ...form,
        commission_value: parseFloat(form.commission_value),
        // Empty strings are not empty values to a database: sent as-is they
        // would store '' where the column means "not set", and the LEFT JOINs
        // that light up the list would then match nothing rather than skipping.
        login_email: form.login_email || undefined,
        discount_code: form.discount_code || undefined,
      });
      // The code is generated server-side unless one was supplied, and it is the
      // thing the operator has to put on a poster — so it is shown rather than
      // left to be found in a table.
      toast.success(t('admin.affiliates.created', { code: created.code }), { duration: 6000 });
      setShowForm(false);
      setForm(EMPTY_FORM);
      load();
    } catch (err) {
      toast.error(errorText(err, t('admin.affiliates.createFailed')));
    } finally {
      setSaving(false);
    }
  };

  const toggle = async (a) => {
    const next = a.status === 'active' ? 'paused' : 'active';
    try {
      await api.patch(`/affiliates/${a.id}`, { status: next });
      toast.success(next === 'active' ? t('admin.affiliates.resumed') : t('admin.affiliates.paused'));
      load();
    } catch (err) {
      toast.error(errorText(err, t('admin.affiliates.updateFailed')));
    }
  };

  const open = async (a) => {
    try {
      setDetail(await api.get(`/affiliates/${a.id}`));
    } catch (err) {
      toast.error(errorText(err, t('admin.loadError')));
    }
  };

  const recordPayout = async () => {
    // The reference is asked for in the same breath as the confirmation, because
    // an operator who is told "enter the reference" AFTER confirming has already
    // decided and will type anything to get past it.
    const reference = window.prompt(
      t('admin.affiliates.payoutPrompt', {
        amount: money(detail.balance.approved),
        name: detail.name,
      })
    );
    if (reference === null) return;

    setPayingOut(true);
    try {
      const payout = await api.post(`/affiliates/${detail.id}/payouts`, { reference });
      toast.success(t('admin.affiliates.payoutRecorded', {
        amount: money(payout.amount), count: payout.commissions,
      }));
      setDetail(await api.get(`/affiliates/${detail.id}`));
      load();
    } catch (err) {
      toast.error(errorText(err, t('admin.affiliates.payoutFailed')));
    } finally {
      setPayingOut(false);
    }
  };

  const kindLabel = (kind) => t(`admin.affiliates.kind.${kind}`);
  const rateLabel = (a) => a.commission_type === 'percentage'
    ? `${parseFloat(a.commission_value)}%`
    : money(a.commission_value);

  return (
    <div style={{ minHeight: '100vh', background: theme.grey100, fontFamily: theme.font.body, display: 'flex' }}>
      <AdminSidebar />

      <div className="admin-main" style={{ padding: '40px' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 40, gap: 16, flexWrap: 'wrap' }}>
          <div>
            <h1 style={{ fontFamily: theme.font.display, fontSize: 40, color: theme.ink, margin: 0 }}>
              {t('admin.affiliates.title')}
            </h1>
            <p style={{ color: theme.grey400, margin: '4px 0 0' }}>
              {t('admin.affiliates.count', { count: affiliates.length })}
            </p>
          </div>
          <button onClick={() => setShowForm(true)} style={primaryBtn}>
            <Plus size={16} /> {t('admin.affiliates.newPartner')}
          </button>
        </div>

        {/* What is owed across everyone, so the operator knows whether a payout
            run is worth doing at all before opening any single partner. */}
        {!loading && affiliates.length > 0 && (
          <div style={{
            display: 'flex', gap: 32, flexWrap: 'wrap', background: theme.white,
            border: `1px solid ${theme.creamBone}`, borderRadius: 4,
            padding: '18px 24px', marginBottom: 24,
          }}>
            <Total label={t('admin.affiliates.totalOwed')}
                   value={money(affiliates.reduce((s, a) => s + a.approved_amount, 0))} accent />
            <Total label={t('admin.affiliates.totalPending')}
                   value={money(affiliates.reduce((s, a) => s + a.pending_amount, 0))} />
            <Total label={t('admin.affiliates.totalPaid')}
                   value={money(affiliates.reduce((s, a) => s + a.paid_amount, 0))} />
          </div>
        )}

        <div style={{ background: theme.white, border: `1px solid ${theme.creamBone}`, borderRadius: 4, overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 14, minWidth: 900 }}>
            <thead>
              <tr style={{ background: theme.cream }}>
                {['colName', 'colKind', 'colCode', 'colRate', 'colReferrals', 'colOwed', 'colPending', 'colStatus', 'colActions']
                  .map((h) => <th key={h} style={th}>{t(`admin.affiliates.${h}`)}</th>)}
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan={9} style={{ padding: 40, textAlign: 'center', color: theme.gold }}>{t('admin.affiliates.loading')}</td></tr>
              ) : affiliates.length === 0 ? (
                <tr><td colSpan={9} style={{ padding: 48, textAlign: 'center', color: theme.grey400 }}>{t('admin.affiliates.none')}</td></tr>
              ) : affiliates.map((a) => (
                <tr key={a.id} style={{ borderBottom: `1px solid ${theme.creamDeep}`, opacity: a.status === 'active' ? 1 : 0.55 }}>
                  <td style={{ padding: '15px 16px' }}>
                    <div style={{ color: theme.ink }}>{a.name}</div>
                    {a.contact_email && <div style={{ fontSize: 12, color: theme.grey400 }}>{a.contact_email}</div>}
                  </td>
                  <td style={td}>{kindLabel(a.kind)}</td>
                  <td style={{ padding: '15px 16px' }}>
                    <span style={{
                      fontFamily: theme.font.mono, background: theme.creamDeep, padding: '4px 10px',
                      borderRadius: 4, fontSize: 13, letterSpacing: '0.05em', color: theme.ink,
                    }}>{a.code}</span>
                  </td>
                  <td style={td}>{rateLabel(a)}</td>
                  <td style={td}>{a.referral_count}</td>
                  <td style={{ ...td, color: a.approved_amount > 0 ? theme.ink : theme.grey400, fontWeight: a.approved_amount > 0 ? 500 : 400 }}>
                    {money(a.approved_amount)}
                  </td>
                  <td style={td}>{money(a.pending_amount)}</td>
                  <td style={{ padding: '15px 16px' }}>
                    <span style={{
                      background: a.status === 'active' ? theme.greenWash : theme.grey100,
                      color: a.status === 'active' ? theme.greenDeep : theme.grey450,
                      padding: '3px 10px', borderRadius: 100, fontSize: 11, fontWeight: 500,
                    }}>{t(`admin.affiliates.status.${a.status}`)}</span>
                  </td>
                  <td style={{ padding: '15px 16px' }}>
                    <div style={{ display: 'flex', gap: 8 }}>
                      <button onClick={() => open(a)} title={t('admin.affiliates.viewTitle')} style={iconBtn}>
                        <Eye size={14} />
                      </button>
                      {/* Terminated is deliberately not reachable from a row
                          button: it is irreversible in effect (the partner loses
                          their dashboard) and belongs behind the detail view. */}
                      {a.status !== 'terminated' && (
                        <button onClick={() => toggle(a)}
                                title={a.status === 'active' ? t('admin.affiliates.pauseTitle') : t('admin.affiliates.resumeTitle')}
                                style={{ ...iconBtn, color: a.status === 'active' ? theme.amber : theme.green }}>
                          {a.status === 'active' ? <ToggleRight size={15} /> : <ToggleLeft size={15} />}
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* ── Create ── */}
      {showForm && (
        <Modal onClose={() => { setShowForm(false); setForm(EMPTY_FORM); }} title={t('admin.affiliates.modalTitle')}>
          <form onSubmit={create}>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
              <Field label={t('admin.affiliates.labelKind')} span={2}>
                <select value={form.kind} onChange={(e) => {
                  const kind = e.target.value;
                  // The rate follows the kind only while it is still a default.
                  // Overwriting a number the operator typed because they then
                  // corrected the dropdown is how a negotiated rate silently
                  // becomes the house one.
                  setForm((f) => ({
                    ...f,
                    kind,
                    commission_value: f.commission_type === 'percentage'
                      && f.commission_value === DEFAULT_RATE[f.kind]
                      ? DEFAULT_RATE[kind]
                      : f.commission_value,
                  }));
                }} style={{ ...input, cursor: 'pointer' }}>
                  <option value="shop">{t('admin.affiliates.kind.shop')}</option>
                  <option value="influencer">{t('admin.affiliates.kind.influencer')}</option>
                  <option value="customer">{t('admin.affiliates.kind.customer')}</option>
                </select>
              </Field>

              <Field label={t('admin.affiliates.labelName')} span={2}>
                <input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} required style={input} />
              </Field>

              <Field label={t('admin.affiliates.labelContactEmail')}>
                <input type="email" value={form.contact_email} onChange={(e) => setForm({ ...form, contact_email: e.target.value })} style={input} />
              </Field>
              <Field label={t('admin.affiliates.labelContactPhone')}>
                <input value={form.contact_phone} onChange={(e) => setForm({ ...form, contact_phone: e.target.value })} style={input} />
              </Field>

              <Field label={t('admin.affiliates.labelLoginEmail')} span={2} hint={t('admin.affiliates.hintLoginEmail')}>
                <input type="email" value={form.login_email} onChange={(e) => setForm({ ...form, login_email: e.target.value })} style={input} />
              </Field>

              <Field label={t('admin.affiliates.labelCommissionType')}>
                <select value={form.commission_type} onChange={(e) => setForm({ ...form, commission_type: e.target.value })} style={{ ...input, cursor: 'pointer' }}>
                  <option value="percentage">{t('admin.affiliates.typePercent')}</option>
                  <option value="fixed">{t('admin.affiliates.typeFixed')}</option>
                </select>
              </Field>
              <Field label={form.commission_type === 'percentage'
                ? t('admin.affiliates.labelPercentage')
                : t('admin.affiliates.labelAmount')}>
                <input type="number" min="0" step="0.01" value={form.commission_value}
                       onChange={(e) => setForm({ ...form, commission_value: e.target.value })} required style={input} />
              </Field>

              <Field label={t('admin.affiliates.labelDiscountCode')} span={2} hint={t('admin.affiliates.hintDiscountCode')}>
                <input value={form.discount_code}
                       onChange={(e) => setForm({ ...form, discount_code: e.target.value.toUpperCase() })}
                       style={{ ...input, fontFamily: theme.font.mono }} />
              </Field>

              <Field label={t('admin.affiliates.labelPayoutMethod')}>
                <select value={form.payout_method} onChange={(e) => setForm({ ...form, payout_method: e.target.value })} style={{ ...input, cursor: 'pointer' }}>
                  <option value="bank_transfer">{t('admin.affiliates.payout.bank_transfer')}</option>
                  <option value="account_credit">{t('admin.affiliates.payout.account_credit')}</option>
                  <option value="other">{t('admin.affiliates.payout.other')}</option>
                </select>
              </Field>
              <Field label={t('admin.affiliates.labelPayoutDetails')}>
                <input value={form.payout_details} onChange={(e) => setForm({ ...form, payout_details: e.target.value })} style={input} />
              </Field>

              <Field label={t('admin.affiliates.labelNotes')} span={2}>
                <textarea value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} rows={2} style={{ ...input, resize: 'vertical' }} />
              </Field>
            </div>

            <p style={{ fontSize: 12, color: theme.grey400, lineHeight: 1.7, marginTop: 18 }}>
              {t('admin.affiliates.codeNote')}
            </p>

            <button type="submit" disabled={saving} style={{ ...primaryBtn, width: '100%', justifyContent: 'center', marginTop: 16, padding: 16 }}>
              <Save size={16} /> {saving ? t('admin.affiliates.creating') : t('admin.affiliates.create')}
            </button>
          </form>
        </Modal>
      )}

      {/* ── Detail ── */}
      {detail && (
        <Modal onClose={() => setDetail(null)} title={detail.name} wide>
          <div style={{ display: 'flex', gap: 28, flexWrap: 'wrap', marginBottom: 24 }}>
            <Total label={t('admin.affiliates.totalOwed')} value={money(detail.balance.approved)} accent />
            <Total label={t('admin.affiliates.totalPending')} value={money(detail.balance.pending)} />
            <Total label={t('admin.affiliates.totalPaid')} value={money(detail.balance.paid)} />
          </div>

          <div style={{ fontSize: 13, color: theme.grey550, lineHeight: 1.8, marginBottom: 20 }}>
            <div><strong>{t('admin.affiliates.colCode')}:</strong> <span style={{ fontFamily: theme.font.mono }}>{detail.code}</span></div>
            <div><strong>{t('admin.affiliates.colRate')}:</strong> {rateLabel(detail)}</div>
            {detail.payout_details && <div><strong>{t('admin.affiliates.labelPayoutDetails')}:</strong> {detail.payout_details}</div>}
            <div>
              <strong>{t('admin.affiliates.termsLabel')}:</strong>{' '}
              {detail.terms_accepted_at
                ? t('admin.affiliates.termsAccepted', {
                    date: new Date(detail.terms_accepted_at).toLocaleDateString(dateLocale),
                    version: detail.terms_version || '—',
                  })
                : <span style={{ color: theme.amber }}>{t('admin.affiliates.termsPending')}</span>}
            </div>
          </div>

          <button
            onClick={recordPayout}
            disabled={payingOut || detail.balance.approved <= 0}
            style={{
              ...primaryBtn,
              opacity: detail.balance.approved > 0 ? 1 : 0.45,
              cursor: detail.balance.approved > 0 ? 'pointer' : 'not-allowed',
              marginBottom: 8,
            }}>
            <Wallet size={16} />
            {payingOut
              ? t('admin.affiliates.recording')
              : t('admin.affiliates.recordPayout', { amount: money(detail.balance.approved) })}
          </button>
          <p style={{ fontSize: 12, color: theme.grey400, lineHeight: 1.7, margin: '0 0 28px' }}>
            {t('admin.affiliates.payoutNote')}
          </p>

          <h3 style={h3}>{t('admin.affiliates.commissionsTitle')}</h3>
          <SimpleTable
            head={[t('admin.affiliates.colDate'), t('admin.affiliates.colBasis'), t('admin.affiliates.colAmount'), t('admin.affiliates.colStatus')]}
            empty={t('admin.affiliates.noCommissions')}
            rows={detail.commissions.map((c) => [
              new Date(c.rental_paid_at || c.created_at).toLocaleDateString(dateLocale),
              money(c.basis_amount),
              money(c.amount),
              t(`admin.affiliates.commissionStatus.${c.status}`),
            ])}
          />

          <h3 style={h3}>{t('admin.affiliates.payoutsTitle')}</h3>
          <SimpleTable
            head={[t('admin.affiliates.colDate'), t('admin.affiliates.colAmount'), t('admin.affiliates.colReference')]}
            empty={t('admin.affiliates.noPayouts')}
            rows={detail.payouts.map((p) => [
              new Date(p.paid_at).toLocaleDateString(dateLocale),
              money(p.amount),
              p.reference || '—',
            ])}
          />
        </Modal>
      )}
    </div>
  );
}

// ── Presentation ───────────────────────────────────────────────────────────

const th = { padding: '14px 16px', textAlign: 'left', color: theme.grey400, fontWeight: 500, fontSize: 12, letterSpacing: '0.08em' };
const td = { padding: '15px 16px', color: theme.grey600, fontSize: 13 };
const h3 = { fontFamily: theme.font.display, fontSize: 19, fontWeight: 400, color: theme.ink, margin: '28px 0 12px' };
const input = {
  width: '100%', padding: '12px 16px', border: `1px solid ${theme.sand}`, borderRadius: 4,
  fontSize: 14, fontFamily: theme.font.body, boxSizing: 'border-box', background: theme.white,
};
const primaryBtn = {
  display: 'inline-flex', alignItems: 'center', gap: 8, background: theme.ink, color: theme.white,
  border: 'none', padding: '14px 24px', borderRadius: 2, cursor: 'pointer',
  fontSize: 13, letterSpacing: '0.1em', fontFamily: theme.font.body,
};
const iconBtn = {
  background: 'none', border: `1px solid ${theme.sand}`, borderRadius: 4,
  padding: '6px 10px', cursor: 'pointer', color: theme.grey600, display: 'flex', alignItems: 'center',
};

function Total({ label, value, accent }) {
  return (
    <div>
      <div style={{ fontSize: 11, color: theme.grey400, letterSpacing: '0.1em', textTransform: 'uppercase' }}>{label}</div>
      <div style={{ fontFamily: theme.font.display, fontSize: 26, color: accent ? theme.ink : theme.grey550 }}>{value}</div>
    </div>
  );
}

function Field({ label, hint, span = 1, children }) {
  return (
    <div style={{ gridColumn: `span ${span}` }}>
      <label style={{ display: 'block', fontSize: 12, color: theme.grey600, letterSpacing: '0.08em', marginBottom: 8 }}>{label}</label>
      {children}
      {hint && <p style={{ margin: '6px 0 0', fontSize: 11, color: theme.grey400, lineHeight: 1.6 }}>{hint}</p>}
    </div>
  );
}

function Modal({ title, onClose, children, wide }) {
  return (
    <div style={{
      position: 'fixed', inset: 0, background: theme.alpha.black(0.5), zIndex: 1000,
      display: 'flex', alignItems: 'flex-start', justifyContent: 'center', padding: 24, overflowY: 'auto',
    }}>
      <div style={{
        background: theme.white, borderRadius: 4, width: '100%',
        maxWidth: wide ? 720 : 520, padding: 40, margin: 'auto',
      }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 28, gap: 16 }}>
          <h2 style={{ fontFamily: theme.font.display, fontSize: 27, margin: 0, fontWeight: 400 }}>{title}</h2>
          <button onClick={onClose} style={{ background: 'none', border: 'none', cursor: 'pointer', color: theme.grey400 }}>
            <X size={20} />
          </button>
        </div>
        {children}
      </div>
    </div>
  );
}

function SimpleTable({ head, rows, empty }) {
  return (
    <div style={{ border: `1px solid ${theme.creamBone}`, borderRadius: 4, overflowX: 'auto' }}>
      <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13, minWidth: 380 }}>
        <thead>
          <tr style={{ background: theme.cream }}>
            {head.map((h) => <th key={h} style={{ ...th, fontSize: 11 }}>{h}</th>)}
          </tr>
        </thead>
        <tbody>
          {rows.length === 0 ? (
            <tr><td colSpan={head.length} style={{ padding: 28, textAlign: 'center', color: theme.grey400 }}>{empty}</td></tr>
          ) : rows.map((cells, i) => (
            <tr key={i} style={{ borderBottom: `1px solid ${theme.creamDeep}` }}>
              {cells.map((c, j) => <td key={j} style={{ ...td, fontSize: 13 }}>{c}</td>)}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
